// @tennis-matches/services/winterdoppel/fairness

import { playerAvailableOnDate } from "./dateUtils.js";
import { buildWinterDoppelPlayerDisplayNames } from "./playerDisplay.js";
import { isWinterDoppelPlanFinal } from "./planLock.js";
import {
  effectiveFairnessDenominator,
  participationCapForFairness,
} from "./participation.js";
import {
  WINTERDOPPEL_FAIRNESS_MODE_DEFAULT,
  normalizeWinterDoppelFairnessMode,
} from "./constants.js";

/**
 * Saison-Fairness je Spieler.
 * Verfügbar: Termine, an denen der Spieler laut Blocks/Wochenregel/Interesse kann.
 * Geplant: aktive Starter-Zuweisungen (rsvp ≠ no).
 * Absagen: rsvp=no (Wochen-Check-in).
 * Quote: Geplant / Verfügbar (bzw. Cap).
 *
 * @param {{
 *   players?: Array<{id:string,display_name?:string,interest_status?:string,week_rule?:string}>,
 *   sessions?: Array<{id:string,session_date?:string,status?:string}>,
 *   sessionPlayers?: Array<{session_id:string,player_id:string,role?:string,rsvp?:string}>,
 *   blocks?: Array<{player_id:string,start_date?:string,end_date?:string}>,
 *   series?: {plan_status?:string,fairness_mode?:string},
 * }} input
 */
export function buildWinterDoppelFairnessRows({
  players = [],
  sessions = [],
  sessionPlayers = [],
  blocks = [],
  series = null,
} = {}) {
  const countAbsagen = isWinterDoppelPlanFinal(series);
  const fairnessMode = normalizeWinterDoppelFairnessMode(
    series?.fairness_mode ?? series?.fairnessMode,
  );
  const activeSessions = (sessions || []).filter(
    (s) => s?.id && s.status !== "cancelled",
  );
  const sessionById = new Map(activeSessions.map((s) => [String(s.id), s]));

  const sessionDates = activeSessions
    .map((s) => String(s.session_date || "").slice(0, 10))
    .filter(Boolean);

  /** @type {Map<string, {verfuegbar: number, geplant: number, absagen: number, cap: number|null}>} */
  const stats = new Map();
  const availabilityByPlayer = buildWinterDoppelAvailabilityByPlayer({
    players,
    sessions: activeSessions,
    blocks,
  });
  for (const p of players || []) {
    if (!p?.id) continue;
    const pid = String(p.id);
    const verfuegbar = availabilityByPlayer.get(pid) || 0;
    stats.set(pid, {
      verfuegbar,
      geplant: 0,
      absagen: 0,
      cap: participationCapForFairness(p, sessionDates),
    });
  }

  for (const sp of sessionPlayers || []) {
    const playerId = String(sp?.player_id || "");
    const sessionId = String(sp?.session_id || "");
    if (!playerId || !sessionId) continue;
    const session = sessionById.get(sessionId);
    if (!session) continue;

    if (!stats.has(playerId)) {
      stats.set(playerId, { verfuegbar: 0, geplant: 0, absagen: 0, cap: null });
    }
    const row = stats.get(playerId);

    if (countAbsagen && sp.rsvp === "no") {
      row.absagen += 1;
    }
    if (sp.role === "starter" && sp.rsvp !== "no") {
      row.geplant += 1;
    }
  }

  const nameById = buildWinterDoppelPlayerDisplayNames(players);

  const rows = [];
  for (const [playerId, { verfuegbar, geplant, absagen, cap }] of stats) {
    const ziel = effectiveFairnessDenominator(verfuegbar, cap);
    rows.push({
      playerId,
      displayName: nameById.get(playerId) || playerId,
      verfuegbar,
      geplant,
      einsaetze: geplant,
      absagen,
      cap,
      quote: ziel > 0 ? geplant / ziel : null,
      fairnessMode,
    });
  }

  rows.sort((a, b) => {
    if (fairnessMode === "equal_load") {
      if (a.geplant !== b.geplant) return a.geplant - b.geplant;
    }
    const qa = a.quote == null ? Number.POSITIVE_INFINITY : a.quote;
    const qb = b.quote == null ? Number.POSITIVE_INFINITY : b.quote;
    if (qa !== qb) return qa - qb;
    if (a.geplant !== b.geplant) return a.geplant - b.geplant;
    if (a.absagen !== b.absagen) return b.absagen - a.absagen;
    return a.displayName.localeCompare(b.displayName, "de");
  });

  return rows;
}

/** Kontext für dynamische Wartelisten-Reihenfolge. */
export function buildWinterDoppelFairnessContext({
  players = [],
  sessions = [],
  sessionPlayers = [],
  blocks = [],
  fairnessMode = WINTERDOPPEL_FAIRNESS_MODE_DEFAULT,
} = {}) {
  const activeSessions = (sessions || []).filter(
    (s) => s?.id && s.status !== "cancelled",
  );
  const sessionDates = activeSessions
    .map((s) => String(s.session_date || "").slice(0, 10))
    .filter(Boolean);
  const availabilityCounts = buildWinterDoppelAvailabilityByPlayer({
    players,
    sessions: activeSessions,
    blocks,
  });
  const participationCaps = new Map(
    (players || [])
      .filter((p) => p?.id)
      .map((p) => [String(p.id), participationCapForFairness(p, sessionDates)]),
  );
  const starterCounts = new Map();
  for (const sp of sessionPlayers || []) {
    if (sp?.role !== "starter" || sp?.rsvp === "no" || !sp?.player_id) continue;
    const pid = String(sp.player_id);
    starterCounts.set(pid, (starterCounts.get(pid) || 0) + 1);
  }
  return {
    starterCounts,
    availabilityCounts,
    participationCaps,
    fairnessMode: normalizeWinterDoppelFairnessMode(fairnessMode),
  };
}

/** Verfügbare Termine je Spieler (für Grobplanung / Quote). */
export function buildWinterDoppelAvailabilityByPlayer({
  players = [],
  sessions = [],
  blocks = [],
} = {}) {
  const activeSessions = (sessions || []).filter(
    (s) => s?.id && s.status !== "cancelled",
  );
  const blocksByPlayer = new Map();
  for (const b of blocks || []) {
    const pid = String(b?.player_id || "");
    if (!pid) continue;
    const list = blocksByPlayer.get(pid) || [];
    list.push(b);
    blocksByPlayer.set(pid, list);
  }

  /** @type {Map<string, number>} */
  const availability = new Map();
  for (const p of players || []) {
    if (!p?.id) continue;
    const pid = String(p.id);
    let verfuegbar = 0;
    const playerBlocks = blocksByPlayer.get(pid) || [];
    for (const s of activeSessions) {
      const date = String(s.session_date || "").slice(0, 10);
      if (!date) continue;
      if (playerAvailableOnDate(p, playerBlocks, date)) verfuegbar += 1;
    }
    availability.set(pid, verfuegbar);
  }
  return availability;
}
